/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 管理团队标签弹窗（仅管理员可见）
 *   功能:
 *     1. 新建标签
 *     2. 修改名称（实时查重、空白防呆、长度限制）
 *     3. 未使用（useCount=0）的标签 → 删除
 *     4. 已使用（useCount>0）的标签 → 停用/启用（停用后不在打标场景下发，但保留历史）
 *     5. 「全员必填」开关 = 后端 forceLabel 配置
 *
 * 删除规则:
 *   - 已使用标签删除会破坏历史照片-标签关联语义，后端拒绝硬删
 *   - 管理员应改用"停用"，停用后历史绑定关系保留，新打标场景不暴露该标签
 */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Input, message, Modal, Spin, Switch } from 'antd'
import {
  CloseOutlined,
  MessageOutlined,
  SettingOutlined
} from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'
import type { RootState } from '../../../app/store'
import {
  createOrUpdateTagGroup,
  createOrUpdateTeamTag,
  deleteLabel,
  fetchTagGroups,
  getPhotoLabelConfig,
  setPhotoLabelConfig
} from '../../photoTag/api'
import { PRESET_COLORS } from '../../photoTag/constants'
import type { PhotoTag } from '../../photoTag/types'
import { updateLabelsAcrossPhotos } from '../../../slices/photosByContentSlice'
import {
  trackPhotoLabelManageEnter,
  trackPhotoLabelManageClick,
  trackPhotoLabelManageToast,
  trackPhotoLabelManageResult,
  trackPhotoLabelFeedbackEnter,
  TagEntryFrom
} from '../../photoTag/utils/track'
import { invalidateTeamPhotoTagsCache } from '../../../app/hooks/useTeamPhotoTags'
import { usePhotoLabelLimits } from '../../../app/hooks/usePhotoLabelLimits'
import FeedbackDrawer from '../../photoTag/components/FeedbackDrawer'
import './index.scss'

export interface PhotoTagManageModalProps {
  visible: boolean
  onClose: () => void
  /** 保存成功回调（外层可刷新列表 / 提示 toast） */
  onSaved?: () => void
  /** v4: 打开来源入口 (引导/管理/其他), 作为 enter 事件的 elementFrom */
  source?: TagEntryFrom
}

interface DraftTag {
  /** 已保存标签的 id；新建标签为 undefined */
  id?: string
  /** 当前编辑中的名称 */
  name: string
  /** 用于变更检测的服务端原始名称 */
  originalName?: string
  color: string
  /** 服务端记录的使用次数，新建为 0 */
  useCount: number
  /** 当前是否处于"停用"状态 */
  disabled: boolean
  /** 服务端记录的原始 disabled 状态（变更检测用） */
  originalDisabled: boolean
  /** 用于 React key */
  key: string
}

let draftKeyCounter = 0
const genKey = () => `dt_${++draftKeyCounter}`

const DEFAULT_TIP_REQUIRED =
  '💡 已开启全员必填 · 员工拍照时会强制要求选择以下标签，未打标无法上传。建议标签数 3-5 个。'
const DEFAULT_TIP_OPTIONAL =
  '💡 当前为建议模式 · 标签会出现在员工拍照页推荐位，但不强制要求。'
const EMPTY_TIP = '💡 点击下方「+ 添加标签」创建你的第一个团队标签'

const formatUseCount = (n: number) => {
  if (n <= 0) return '未使用'
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k 次`
  return `${n} 次`
}

const PhotoTagManageModal: React.FC<PhotoTagManageModalProps> = ({
  visible,
  onClose,
  onSaved,
  source = 'other'
}) => {
  const dispatch = useDispatch()
  const groupID = useSelector((state: RootState) => state.group.groupId)
  const userID = useSelector(
    (state: RootState) => state.main.user?.user_id
  ) as string

  const [loading, setLoading] = useState(false)
  const [feedbackVisible, setFeedbackVisible] = useState(false)
  const [saving, setSaving] = useState(false)
  const [tags, setTags] = useState<DraftTag[]>([])
  const [forceLabel, setForceLabel] = useState(false)
  /** 团队标签默认组 id（用于新建标签时挂载） */
  const defaultGroupIdRef = useRef<string | null>(null)
  /** 记录在 loadAll 之后被用户主动删除的服务端标签 id，保存时调 deleteLabel */
  const pendingDeleteIdsRef = useRef<string[]>([])
  /** 名字重复的 key（实时查重提示用，最多一处） */
  const [duplicateKey, setDuplicateKey] = useState<string | null>(null)
  /** 团队标签数量上限 (KV 可覆盖, 回落本地常量), 用于「+ 添加标签」拦截 */
  const { teamTagLimitPerGroup } = usePhotoLabelLimits()

  /** 拉取数据：标签 + config */
  const loadAll = useCallback(async () => {
    if (!groupID || !userID) return
    setLoading(true)
    pendingDeleteIdsRef.current = []
    setDuplicateKey(null)
    try {
      const [groups, config] = await Promise.all([
        fetchTagGroups(String(groupID), userID),
        getPhotoLabelConfig(String(groupID))
      ])
      // 一期产品约定「每团队 1 个标签组」，新建标签都挂到第 0 个组上（defaultGroupIdRef）。
      // 但 DB 历史脏数据可能存在多个 label_group（例：同一团队早期建过 2 个组），
      // 之前只读 groups[0] 会丢掉其它组下的标签 — 卡片上显示了「测试3」但管理弹窗里看不到。
      // 这里把所有 group 的 tags 打平展示，让历史标签都能被编辑/停用/删除，兜底脏数据。
      const firstGroup = groups[0] || null
      defaultGroupIdRef.current = firstGroup?.id ?? null
      const flatTags: PhotoTag[] = groups.reduce<PhotoTag[]>(
        (acc, g) => acc.concat(g.tags || []),
        []
      )
      setTags(
        flatTags.map((t) => ({
          id: t.id,
          name: t.name,
          originalName: t.name,
          color: t.color || PRESET_COLORS[0],
          useCount: t.useCount ?? 0,
          disabled: Boolean(t.disabled),
          originalDisabled: Boolean(t.disabled),
          key: genKey()
        }))
      )
      setForceLabel(Boolean(config.forceLabel))
    } catch (err) {
      message.error((err as Error)?.message || '加载标签数据失败')
    } finally {
      setLoading(false)
    }
  }, [groupID, userID])

  useEffect(() => {
    if (visible) {
      loadAll()
    }
  }, [visible, loadAll])

  // 埋点: enter 半屏（visible 切 true 时，等 tags load 完上报真实标签数）
  useEffect(() => {
    if (visible && !loading) {
      trackPhotoLabelManageEnter({
        tabName: 'team_label', // 一期只有 team_label tab
        number1: tags.filter((t) => !t.disabled).length,
        elementFrom: source // v4: 来源入口 (引导/管理/其他)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, loading])

  /** 实时检测重名：返回当前重复的 key（取第一个出现） */
  const checkDuplicate = (list: DraftTag[]) => {
    const seen = new Map<string, string>() // name -> firstKey
    for (const t of list) {
      const trimmed = t.name.trim()
      if (!trimmed) continue
      if (seen.has(trimmed)) {
        // 第二次出现的算冲突
        return t.key
      }
      seen.set(trimmed, t.key)
    }
    return null
  }

  const updateName = (key: string, name: string) => {
    setTags((prev) => {
      const next = prev.map((t) => (t.key === key ? { ...t, name } : t))
      setDuplicateKey(checkDuplicate(next))
      return next
    })
  }

  const toggleDisabled = (key: string) => {
    const target = tags.find((t) => t.key === key)
    if (target) {
      const nextDisabled = !target.disabled
      trackPhotoLabelManageClick({
        clickItem: nextDisabled ? 'toggle_disable' : 'toggle_enable',
        tabName: 'team_label',
        string1: target.id,
        labelName: target.name,
        bool1: nextDisabled
      })
    }
    setTags((prev) =>
      prev.map((t) => (t.key === key ? { ...t, disabled: !t.disabled } : t))
    )
  }

  const removeTag = (key: string) => {
    setTags((prev) => {
      const target = prev.find((t) => t.key === key)
      // 埋点: click delete_label（针对已保存的）— 一期管理半屏没有二次确认弹窗，
      // 直接从列表删，保存时调 deleteLabel.
      if (target?.id) {
        trackPhotoLabelManageClick({
          clickItem: 'delete_label',
          tabName: 'team_label',
          string1: target.id,
          labelName: target.name
        })
        pendingDeleteIdsRef.current.push(target.id)
      } else {
        // 未保存的新建行被移除 = 取消本次新建 (无 id, 不进 pendingDelete)
        trackPhotoLabelManageClick({
          clickItem: 'cancel_add_label',
          tabName: 'team_label',
          labelName: target?.name || undefined
        })
      }
      const next = prev.filter((t) => t.key !== key)
      setDuplicateKey(checkDuplicate(next))
      return next
    })
  }

  const addTag = () => {
    // 达到团队标签数量上限 → 拦截 + 上报 label_count_limit, 不再走新建
    if (tags.length >= teamTagLimitPerGroup) {
      message.warning(`最多创建 ${teamTagLimitPerGroup} 个团队标签`)
      trackPhotoLabelManageToast({
        toastName: 'label_count_limit',
        tabName: 'team_label'
      })
      return
    }
    // 埋点: click add_label
    trackPhotoLabelManageClick({
      clickItem: 'add_label',
      tabName: 'team_label'
    })
    setTags((prev) => [
      ...prev,
      {
        name: '',
        color: PRESET_COLORS[prev.length % PRESET_COLORS.length],
        useCount: 0,
        disabled: false,
        originalDisabled: false,
        key: genKey()
      }
    ])
    // 滚到底
    setTimeout(() => {
      const list = document.querySelector('.tag-manage-list')
      if (list) list.scrollTop = list.scrollHeight
    }, 0)
  }

  /** 关闭半屏：埋点 click close + 调外层 onClose */
  const handleClose = useCallback(() => {
    trackPhotoLabelManageClick({
      clickItem: 'close',
      tabName: 'team_label'
    })
    onClose()
  }, [onClose])

  /** 保存：删除 → 创建/改名 → disabled 切换 → config */
  const handleSave = async () => {
    if (!groupID || !userID) return

    // 埋点: click confirm_add_label（语义复用：保存按钮一次性处理新建/改名/删除/disabled）
    trackPhotoLabelManageClick({
      clickItem: 'confirm_add_label',
      tabName: 'team_label'
    })
    // 校验：每条名称不能为空、不超过 12 字
    const cleaned = tags.map((t) => ({ ...t, name: t.name.trim() }))
    for (const t of cleaned) {
      if (!t.name) {
        message.warning('标签名称不能为空')
        trackPhotoLabelManageToast({ toastName: 'label_name_empty' })
        return
      }
      if (t.name.length > 12) {
        message.warning('单个标签名称不超过 12 字')
        trackPhotoLabelManageToast({ toastName: 'label_name_too_long' })
        return
      }
    }
    // 校验：同一弹窗内不能重名
    if (checkDuplicate(cleaned)) {
      message.warning('标签名称不能重复')
      trackPhotoLabelManageToast({ toastName: 'label_name_duplicate' })
      return
    }

    setSaving(true)
    // 在 try 之前快照，用于保存成功后做跨照片乐观更新
    // （改名：对比 originalName；删除：走 pendingDeleteIdsRef）
    const deleteIdsSnapshot = [...pendingDeleteIdsRef.current]
    const renamedSnapshot = cleaned
      .filter((t) => t.id && t.name !== t.originalName)
      .map((t) => ({ id: t.id as string, name: t.name, color: t.color }))
    // 记录失败发生在哪一步, catch 里据此上报对应的 *_fail toast
    let failedAction: 'create' | 'update' | 'delete' | null = null
    try {
      // 1. 删除被移除的标签（并发：都是 useCount=0，后端独立处理）
      const deleteIds = [...pendingDeleteIdsRef.current]
      if (deleteIds.length > 0) {
        const deletedTagInfo = tags.filter(
          (t) => t.id && deleteIds.includes(t.id)
        )
        const t0d = Date.now()
        try {
          await Promise.all(
            deleteIds.map((id) => deleteLabel(String(groupID), id))
          )
          // 每个被删的 tag 各上报一次 result
          deleteIdsSnapshot.forEach((id) => {
            const tag = deletedTagInfo.find((t) => t.id === id)
            trackPhotoLabelManageResult({
              actionName: 'delete_label',
              actionResult: 'success',
              costTime: Date.now() - t0d,
              string1: id,
              labelName: tag?.name || '',
              tabName: 'team_label'
            })
          })
          pendingDeleteIdsRef.current = []
        } catch (e) {
          failedAction = 'delete'
          deleteIdsSnapshot.forEach((id) => {
            const tag = deletedTagInfo.find((t) => t.id === id)
            trackPhotoLabelManageResult({
              actionName: 'delete_label',
              actionResult: 'fail',
              costTime: Date.now() - t0d,
              errorMsg: (e as Error)?.message,
              string1: id,
              labelName: tag?.name || '',
              tabName: 'team_label'
            })
          })
          throw e
        }
      }

      // 2. 确保有 default group（无则创建）
      let groupId = defaultGroupIdRef.current
      if (!groupId && cleaned.length > 0) {
        const newGroup = await createOrUpdateTagGroup(String(groupID), userID, {
          name: '团队标签'
        })
        groupId = newGroup.id
        defaultGroupIdRef.current = groupId
      }

      // 3. 串行处理：新建 / 改名 / disabled 切换
      for (const t of cleaned) {
        const isNew = !t.id
        const renamed = !isNew && t.name !== t.originalName
        const disabledChanged = !isNew && t.disabled !== t.originalDisabled

        if (isNew) {
          const t0c = Date.now()
          try {
            await createOrUpdateTeamTag(String(groupID), {
              name: t.name,
              color: t.color,
              groupId: groupId || undefined
            })
            trackPhotoLabelManageResult({
              actionName: 'create_label',
              actionResult: 'success',
              costTime: Date.now() - t0c,
              labelName: t.name,
              tabName: 'team_label'
            })
          } catch (e) {
            failedAction = 'create'
            trackPhotoLabelManageResult({
              actionName: 'create_label',
              actionResult: 'fail',
              costTime: Date.now() - t0c,
              errorMsg: (e as Error)?.message,
              labelName: t.name,
              tabName: 'team_label'
            })
            throw e
          }
          continue
        }

        if (renamed || disabledChanged) {
          const t0u = Date.now()
          try {
            await createOrUpdateTeamTag(String(groupID), {
              id: t.id,
              name: t.name,
              color: t.color,
              disabled: disabledChanged ? t.disabled : undefined
            })
            trackPhotoLabelManageResult({
              actionName: 'update_label',
              actionResult: 'success',
              costTime: Date.now() - t0u,
              string1: t.id,
              labelName: t.name,
              tabName: 'team_label'
            })
          } catch (e) {
            failedAction = 'update'
            trackPhotoLabelManageResult({
              actionName: 'update_label',
              actionResult: 'fail',
              costTime: Date.now() - t0u,
              errorMsg: (e as Error)?.message,
              string1: t.id,
              labelName: t.name,
              tabName: 'team_label'
            })
            throw e
          }
        }
      }

      // 4. 写 config（forceLabel）
      await setPhotoLabelConfig(String(groupID), {
        forceLabel,
        remindWhenNotForce: true,
        remindScenes: ['preview', 'watermark', 'menu']
      })

      // 跨照片乐观更新：让卡片底部 chip 立即拿到新名/新色，删除的 label 立即消失。
      // 不等用户手动刷新页面 — 之前是改名后必须 F5 才能看到新名，体验崩。
      // 注意只覆盖 photosByContent slice（photov2 + ai-photo-king 共用），旧 photo 页另算。
      if (renamedSnapshot.length > 0 || deleteIdsSnapshot.length > 0) {
        dispatch(
          updateLabelsAcrossPhotos({
            renamed: renamedSnapshot,
            deletedIds: deleteIdsSnapshot
          })
        )
      }
      // 失效 useTeamPhotoTags 缓存，让卡片 popover / 批量 picker 拉到最新标签列表
      invalidateTeamPhotoTagsCache(String(groupID), userID)

      message.success('已保存')
      // 埋点: toast 成功 — 单条聚合上报（创建/改名/删除分别在各自 result 里已经报过）
      if (renamedSnapshot.length > 0) {
        trackPhotoLabelManageToast({
          toastName: 'update_label_success',
          tabName: 'team_label'
        })
      }
      if (deleteIdsSnapshot.length > 0) {
        trackPhotoLabelManageToast({
          toastName: 'delete_label_success',
          tabName: 'team_label'
        })
      }
      const createdCount = cleaned.filter((t) => !t.id).length
      if (createdCount > 0) {
        trackPhotoLabelManageToast({
          toastName: 'create_label_success',
          tabName: 'team_label'
        })
      }
      onSaved && onSaved()
      onClose()
    } catch (err) {
      const msg = (err as Error)?.message || '保存失败，请重试'
      message.error(msg)
      // 按失败步骤上报对应的 *_fail toast (各自 result 明细已在内层 try/catch 报过)
      const failToast =
        failedAction === 'create'
          ? 'create_label_fail'
          : failedAction === 'delete'
            ? 'delete_label_fail'
            : 'update_label_fail'
      trackPhotoLabelManageToast({
        toastName: failToast,
        tabName: 'team_label'
      })
    } finally {
      setSaving(false)
    }
  }

  const isEmpty = tags.length === 0
  const tipText = isEmpty
    ? EMPTY_TIP
    : forceLabel
      ? DEFAULT_TIP_REQUIRED
      : DEFAULT_TIP_OPTIONAL
  const hasError = duplicateKey !== null

  return (
    <Modal
      wrapClassName="photo-tag-manage-modal-wrap"
      open={visible}
      title={null}
      footer={null}
      closable={false}
      centered
      destroyOnHidden
      width={520}
      onCancel={handleClose}
      mask={{ closable: !saving }}
    >
      {/* Header */}
      <div className="tag-manage-header">
        <h3 className="tag-manage-title">
          <SettingOutlined /> 管理团队标签
        </h3>
        <span className="tag-manage-close" onClick={handleClose}>
          <CloseOutlined />
        </span>
      </div>

      {/* 全员必填开关 + 提示（挪到顶部，跟黄色提醒放一起） */}
      <div className="tag-manage-required">
        <span className="tag-manage-required-label">全员必填</span>
        <Switch
          checked={forceLabel}
          onChange={(checked) => {
            trackPhotoLabelManageClick({
              clickItem: 'toggle_force_label',
              tabName: 'team_label',
              bool1: checked
            })
            setForceLabel(checked)
          }}
          disabled={saving}
        />
      </div>
      <div className="tag-manage-tip">{tipText}</div>

      {/* 列表 */}
      <div className="tag-manage-list">
        {loading ? (
          <div className="tag-manage-loading">
            <Spin />
          </div>
        ) : isEmpty ? (
          <div className="tag-manage-empty">
            <div className="tag-manage-empty__icon">📌</div>
            <div className="tag-manage-empty__title">还没有团队标签</div>
            <div className="tag-manage-empty__hint">
              点击下方「+ 添加标签」开始
            </div>
          </div>
        ) : (
          tags.map((t) => {
            const isUsed = t.useCount > 0
            const isDuplicate = duplicateKey === t.key
            return (
              <div
                className={
                  'tag-manage-row' +
                  (t.disabled ? ' tag-manage-row--disabled' : '') +
                  (isDuplicate ? ' tag-manage-row--error' : '')
                }
                key={t.key}
              >
                {/* 状态点：橙=启用 / 灰=停用 */}
                <span
                  className="tag-manage-row__dot"
                  style={{
                    background: t.disabled
                      ? '#D1D5DB'
                      : forceLabel
                        ? '#FF5C00'
                        : '#86909C'
                  }}
                />
                <Input
                  className="tag-manage-row__input"
                  value={t.name}
                  placeholder="标签名称"
                  maxLength={12}
                  disabled={t.disabled}
                  onChange={(e) => updateName(t.key, e.target.value)}
                />

                {/* 使用次数 / 重复提示：同一行只显示一个 */}
                {isDuplicate ? (
                  <span className="tag-manage-row__error">名称重复</span>
                ) : (
                  <span
                    className={
                      'tag-manage-row__usage' +
                      (isUsed ? '' : ' tag-manage-row__usage--empty')
                    }
                  >
                    {formatUseCount(t.useCount)}
                  </span>
                )}

                {/* 操作：hover 显示（停用状态下常驻显示"启用"） */}
                <div className="tag-manage-row__ops">
                  {isUsed ? (
                    <span
                      className={
                        'tag-manage-row__op' +
                        (t.disabled ? ' tag-manage-row__op--accent' : '')
                      }
                      onClick={() => toggleDisabled(t.key)}
                    >
                      {t.disabled ? '启用' : '停用'}
                    </span>
                  ) : (
                    <span
                      className="tag-manage-row__op tag-manage-row__op--danger"
                      onClick={() => removeTag(t.key)}
                    >
                      删除
                    </span>
                  )}
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* + 添加 */}
      {!loading && (
        <button className="tag-manage-add" onClick={addTag}>
          + 添加标签
        </button>
      )}

      {/* Footer */}
      <div className="tag-manage-footer">
        <Button
          type="link"
          className="tag-manage-footer__feedback"
          onClick={() => {
            trackPhotoLabelFeedbackEnter({ elementFrom: 'manage' })
            setFeedbackVisible(true)
          }}
        >
          <MessageOutlined /> 问题反馈
        </Button>
        <div className="tag-manage-footer__actions">
          <Button onClick={handleClose} disabled={saving}>
            取消
          </Button>
          <Button
            type="primary"
            onClick={handleSave}
            loading={saving}
            disabled={isEmpty || hasError}
          >
            保存
          </Button>
        </div>
      </div>

      <FeedbackDrawer
        visible={feedbackVisible}
        onClose={() => setFeedbackVisible(false)}
        source="manage"
      />
    </Modal>
  )
}

export default PhotoTagManageModal
